import React from "react";
import { FormGroup, Input } from "reactstrap";
import TriStateSwitch from "../../../components/TriStateSwitch";
import { useQuoteItemTableUpdater } from "../../../hooks/useQuoteItemTableUpdater";
import { useLockState } from '../../../components/userLocks/LockCheck';

const NestedTableSwitchColumn = ({
	row,
    quoteItems,
    updateQuoteItem,
    updateQuoteItems,
    propertyName,
    invertValue = false // If true, the switch will be on when the property is false, and off when the property is true.  You could use this, for example, if you have a property like "disabled" but you want to show a switch for "enabled" in the UI.
}) => {
        const [editRow] = useQuoteItemTableUpdater(quoteItems, updateQuoteItem, updateQuoteItems);
        const lockState = useLockState();

        if (!row.items) {
            // This is a line item

            const rowIsBasketProcessingItem = row.source?.toLocaleLowerCase() === "basket processing";

            // In individual item rows, show a switch to toggle defined property on the item
            return (
            <FormGroup switch className="me-3">
                <Input
                    type="switch"
                    checked={invertValue ? !row[propertyName] : row[propertyName]}
                    disabled={lockState?.isLocked || rowIsBasketProcessingItem}
                    title={rowIsBasketProcessingItem ? "Items from basket processing are always included" : undefined}
                    onChange={(e) => {
                        const updatedSwitchState = e.target.checked;

                        editRow(row, propertyName, invertValue ? !updatedSwitchState : updatedSwitchState);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={invertValue ? "Toggle included" : "Toggle excluded"}
                />
            </FormGroup>
        )} else {
            // This is a group, so all child items need to be updated.  Basket processing items will not be modified

            const userItems = row.items.filter(item => item.source?.toLocaleLowerCase() !== "basket processing");
            const autogenItems = row.items.filter(item => item.source?.toLocaleLowerCase() === "basket processing");

            // Helper functions to count how many items in the group have the property on vs off, separating user items from autogen items
            const isPropertyOn = (item) => !!item[propertyName];
            const countItemsWithPropertyOn = (items) => items.reduce((count, item) => count + (isPropertyOn(item) ? 1 : 0), 0);
            const countItemsWithPropertyOff = (items) => items.reduce((count, item) => count + (!isPropertyOn(item) ? 1 : 0), 0);

            // Count how many items in the group have the property on vs off, separating user items from autogen items, 
            // because only user items can be toggled, but we want to show the state of all items in the group in the
            // group-level switch.  For example, if there are 3 items in the group, 2 user items that are on and 1 autogen
            // item that is off, then the group switch should show as "on" with an indeterminate state, because some but not
            // all of the items in the group are on.
            const numUserItemsWithPropertyOn = countItemsWithPropertyOn(userItems);
            const numUserItemsWithPropertyOff = countItemsWithPropertyOff(userItems);
            const numAutogenItemsWithPropertyOn = countItemsWithPropertyOn(autogenItems);
            const numAutogenItemsWithPropertyOff = countItemsWithPropertyOff(autogenItems);

            // If invertValue is true, then the switch is on when the property is false, and vice-versa
            const numUserSwitchesThatAreOn = invertValue ? numUserItemsWithPropertyOff : numUserItemsWithPropertyOn; 
            const numAutogenSwitchesThatAreOn = invertValue ? numAutogenItemsWithPropertyOff : numAutogenItemsWithPropertyOn;

            // Check if there are any items in the group that can be toggled, to determine the state of the group switch 
            // and whether it should be disabled
            const hasUserItems = userItems.length > 0;
            const hasAutogenItems = autogenItems.length > 0;

            // In group rows, show a three-state switch: on when all items are on,
            // off when all items are off, and indeterminate when the group is mixture of on and off.
            const allUserSwitchesAreOn = numUserSwitchesThatAreOn === userItems.length;
            const allAutogenSwitchesAreOn = numAutogenSwitchesThatAreOn === autogenItems.length;
            const allUserSwitchesAreOff = numUserSwitchesThatAreOn === 0;
            const allAutogenSwitchesAreOff = numAutogenSwitchesThatAreOn === 0;
            const allSwitchesAreOn = allUserSwitchesAreOn && allAutogenSwitchesAreOn;
            const allSwitchesAreOff = allUserSwitchesAreOff && allAutogenSwitchesAreOff;
            const isIndeterminate = !allSwitchesAreOn && !allSwitchesAreOff;

            return (
                <FormGroup switch className="me-3">
                    <TriStateSwitch
                        checked={allSwitchesAreOn}
                        indeterminate={isIndeterminate}
                        disabled={lockState?.isLocked || !hasUserItems}
                        title={!hasUserItems ? "This group only contains basket processing items, which are always included" : undefined}
                        ariaLabel="Group included/excluded state"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {

                            let updatedSwitchState;
                            
                            if (hasAutogenItems && !hasUserItems) {
                                // If there are only autogen items in the group, then we cannot change anything, as autogen items cannot be toggled.                               
                                return;
                            } else if (!hasAutogenItems) {
                                // If there are no autogen items in the group, then we can simply toggle all user items to the same state
                                updatedSwitchState = !allUserSwitchesAreOn;                                
                            } else  {
                                // If there are both autogen and user items in the group, then we can only toggle user items

                                if (allUserSwitchesAreOn) {
                                    updatedSwitchState = false;
                                } else if (allUserSwitchesAreOff) {
                                    updatedSwitchState = true;
                                } else {
                                    // If some switches are on and some are off, then we will see if any user items are off.  If there are, then we
                                    // will turn them all on.  If all user items are already on, then we will turn them all off.
                                    updatedSwitchState = numUserSwitchesThatAreOn === userItems.length ? false : true;
                                }
                            }

                            // Because this is a parent row, it will apply the update to all child items
                            editRow(row, propertyName, invertValue ? !updatedSwitchState : updatedSwitchState);
                        }}
                    />
                </FormGroup>
            )
        }
};

export default NestedTableSwitchColumn;
